import { type SerializedHighlight } from '@/src/lib';

// ── ID generation ────────────────────────────────────────────────────

export function generateHighlightId(): string {
    return `hl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ── Flat-offset helpers ──────────────────────────────────────────────

/**
 * Walk every text node under `root` and return the cumulative character
 * offset at the point (targetNode, targetOffset).
 */
function getFlatOffset(root: Node, targetNode: Node, targetOffset: number): number {
    let offset = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Text | null;

    while ((node = walker.nextNode() as Text | null)) {
        if (node === targetNode) {
            return offset + targetOffset;
        }
        offset += node.length;
    }
    return offset;
}

/**
 * Return the text nodes (and their local sub-ranges) that overlap
 * with [start, end) in the flat text of `root`.
 */
function findTextNodesInRange(
    root: Node,
    start: number,
    end: number
): Array<{ node: Text; start: number; end: number }> {
    const result: Array<{ node: Text; start: number; end: number }> = [];
    let offset = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Text | null;

    while ((node = walker.nextNode() as Text | null)) {
        const nodeEnd = offset + node.length;
        if (nodeEnd > start && offset < end) {
            result.push({
                node,
                start: Math.max(0, start - offset),
                end: Math.min(node.length, end - offset),
            });
        }
        offset = nodeEnd;
        if (offset >= end) {
            break;
        }
    }
    return result;
}

// ── Selection → Highlight ────────────────────────────────────────────

/**
 * Convert the current browser Selection into a SerializedHighlight
 * relative to `root`. Returns null if there is no usable selection
 * inside the root element.
 */
export function getSelectionAsHighlight(root: HTMLElement): SerializedHighlight | null {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return null;
    }

    const range = selection.getRangeAt(0);

    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) {
        return null;
    }

    const rawStart = getFlatOffset(root, range.startContainer, range.startOffset);
    const rawEnd = getFlatOffset(root, range.endContainer, range.endOffset);
    const text = selection.toString().trim();

    if (rawStart === rawEnd || !text) {
        return null;
    }

    return {
        id: generateHighlightId(),
        start: Math.min(rawStart, rawEnd),
        end: Math.max(rawStart, rawEnd),
        text,
    };
}

/**
 * Return the plain-text content of whatever the user currently has
 * selected inside `root`, or '' if nothing.
 */
export function getSelectedText(root: HTMLElement): string {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
        return '';
    }
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer)) {
        return '';
    }
    return selection.toString().trim();
}

// ── DOM mutation: apply / strip marks ────────────────────────────────

/**
 * Remove every `<mark data-highlight-id>` that we previously injected,
 * re-merging the split text nodes.
 */
export function stripHighlightMarks(root: HTMLElement): void {
    const marks = root.querySelectorAll('mark[data-highlight-id]');
    marks.forEach((mark) => {
        const parent = mark.parentNode;
        if (!parent) {
            return;
        }
        while (mark.firstChild) {
            parent.insertBefore(mark.firstChild, mark);
        }
        parent.removeChild(mark);
        parent.normalize();
    });
}

/**
 * Inject `<mark>` wrappers for every highlight in the list.
 *
 * Highlights are applied from back-to-front (highest offset first) so
 * that earlier offsets stay valid after each insertion.
 */
export function applyHighlightMarks(root: HTMLElement, highlights: SerializedHighlight[]): void {
    stripHighlightMarks(root);

    if (highlights.length === 0) {
        return;
    }

    const sorted = [...highlights].sort((a, b) => b.start - a.start);

    for (const highlight of sorted) {
        const textNodes = findTextNodesInRange(root, highlight.start, highlight.end);

        // Wrap from last node to first to preserve earlier positions
        for (let i = textNodes.length - 1; i >= 0; i--) {
            const { node, start: s, end: e } = textNodes[i];

            const range = document.createRange();
            range.setStart(node, s);
            range.setEnd(node, e);

            const mark = document.createElement('mark');
            mark.setAttribute('data-highlight-id', highlight.id);
            mark.className = 'exam-highlight';

            try {
                range.surroundContents(mark);
            } catch {
                // Cross-element boundary – extract then re-insert
                const fragment = range.extractContents();
                mark.appendChild(fragment);
                range.insertNode(mark);
            }
        }
    }
}

// ── Hit-testing ──────────────────────────────────────────────────────

/**
 * If `element` (or an ancestor up to the wrapper root) is a highlight
 * mark, return its id. Otherwise null.
 */
export function getHighlightIdFromElement(element: HTMLElement): string | null {
    const mark = element.closest('mark[data-highlight-id]');
    return mark?.getAttribute('data-highlight-id') ?? null;
}

/**
 * Return the full text of a highlight mark by its id, searching within `root`.
 */
export function getHighlightTextById(root: HTMLElement, id: string): string {
    const marks = root.querySelectorAll(`mark[data-highlight-id="${id}"]`);
    return Array.from(marks)
        .map((m) => m.textContent ?? '')
        .join('');
}

// ── Overlap merging ──────────────────────────────────────────────────

/**
 * Add `newHighlight` to the list, merging any highlights whose ranges
 * overlap with it into a single combined highlight.
 */
export function mergeOverlappingHighlights(
    existing: SerializedHighlight[],
    newHighlight: SerializedHighlight
): SerializedHighlight[] {
    const overlapping: SerializedHighlight[] = [];
    const nonOverlapping: SerializedHighlight[] = [];

    for (const h of existing) {
        if (h.start < newHighlight.end && h.end > newHighlight.start) {
            overlapping.push(h);
        } else {
            nonOverlapping.push(h);
        }
    }

    if (overlapping.length === 0) {
        return [...existing, newHighlight];
    }

    const allStarts = [...overlapping.map((h) => h.start), newHighlight.start];
    const allEnds = [...overlapping.map((h) => h.end), newHighlight.end];

    const merged: SerializedHighlight = {
        id: newHighlight.id,
        start: Math.min(...allStarts),
        end: Math.max(...allEnds),
        text: newHighlight.text,
    };

    return [...nonOverlapping, merged];
}
